
import React, { createContext, useContext, ReactNode, useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

interface FreeTrial {
  id: string;
  user_id: string | null;
  email: string;
  started_at: string;
  expires_at: string;
  status: 'active' | 'expired' | 'converted';
  converted_to_subscription_id?: string | null;
  created_at: string;
  updated_at: string;
}

interface TrialContextType {
  trial: FreeTrial | null;
  isLoading: boolean;
  formatTimeRemaining: (ms: number) => string;
  startFreeTrial: (email: string) => Promise<any>;
  refreshTrialStatus: () => Promise<void>;
  hasActiveTrial: boolean;
  hasExpiredTrial: boolean;
  isTrialExpired: boolean;
  hasActiveSubscription: boolean;
}

const TrialContext = createContext<TrialContextType | undefined>(undefined);

export const TrialProvider = ({ children }: { children: ReactNode }) => {
  const [trial, setTrial] = useState<FreeTrial | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [hasActiveSubscription, setHasActiveSubscription] = useState(false);
  const { toast } = useToast();

  // Stable format function
  const formatTimeRemaining = (ms: number) => {
    const safeMs = Math.max(0, ms);
    const hours = Math.floor(safeMs / (1000 * 60 * 60));
    const minutes = Math.floor((safeMs % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((safeMs % (1000 * 60)) / 1000);
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  };

  const fetchTrialStatus = useCallback(async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        setTrial(null);
        setHasActiveSubscription(false);
        return;
      }

      // Check subscription status first
      const { data: subscriptionData } = await supabase
        .from('user_subscriptions')
        .select('*')
        .eq('user_id', session.user.id)
        .eq('status', 'active')
        .maybeSingle();

      if (subscriptionData) {
        setTrial(null);
        setHasActiveSubscription(true);
        return;
      }

      setHasActiveSubscription(false);

      // Fetch trial data
      const { data, error } = await supabase
        .from('free_trials')
        .select('*')
        .eq('user_id', session.user.id)
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (error) throw error;

      if (data && data.status !== 'cancelled') {
        const trialData = data as FreeTrial;
        setTrial(trialData);

        // Check if trial should be expired
        if (trialData.status === 'active') {
          const expiresAt = new Date(trialData.expires_at).getTime();
          const now = Date.now();
          
          if (now >= expiresAt) {
            await supabase
              .from('free_trials')
              .update({ status: 'expired', updated_at: new Date().toISOString() })
              .eq('id', trialData.id);
            setTrial({ ...trialData, status: 'expired' });
          }
        }
      } else {
        setTrial(null);
      }
    } catch (error) {
      console.error('Error fetching trial status:', error);
      setTrial(null);
      setHasActiveSubscription(false);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const startFreeTrial = useCallback(async (email: string) => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        throw new Error('User must be authenticated to start trial');
      }

      // Check if user already has a trial
      const { data: existingTrial } = await supabase
        .from('free_trials')
        .select('*')
        .eq('user_id', session.user.id)
        .maybeSingle();

      if (existingTrial) {
        if (existingTrial.status === 'active') {
          toast({
            title: "Trial Already Active",
            description: "You already have an active free trial.",
            className: "bg-blue-50 border-blue-200",
          });
          setTrial(existingTrial as FreeTrial);
          return existingTrial;
        } else {
          toast({
            title: "Trial Already Used",
            description: "You have already used your free trial. Please upgrade to continue.",
            variant: "destructive",
          });
          throw new Error('Trial already used');
        }
      }

      // Create new trial
      const { data, error } = await supabase
        .from('free_trials')
        .insert({
          user_id: session.user.id,
          email: email,
        })
        .select()
        .single();

      if (error) throw error;

      if (data && data.status !== 'cancelled') {
        setTrial(data as FreeTrial);
        toast({
          title: "Free Trial Started!",
          description: "You now have 24 hours of full access to CSO Agent.",
          className: "bg-green-50 border-green-200",
        });
        await fetchTrialStatus();
        return data;
      }
    } catch (error: any) {
      console.error('Error starting trial:', error);
      toast({
        title: "Error Starting Trial",
        description: error.message || "Failed to start trial. Please try again.",
        variant: "destructive",
      });
      throw error;
    }
  }, [toast, fetchTrialStatus]);

  // Initial fetch
  useEffect(() => {
    fetchTrialStatus();
  }, [fetchTrialStatus]);

  const contextValue = React.useMemo(() => {
    const hasActiveTrial = trial?.status === 'active';
    const hasExpiredTrial = trial?.status === 'expired';

    return {
      trial,
      isLoading,
      formatTimeRemaining,
      startFreeTrial,
      refreshTrialStatus: fetchTrialStatus,
      hasActiveTrial,
      hasExpiredTrial,
      isTrialExpired: hasExpiredTrial,
      hasActiveSubscription,
    };
  }, [trial, isLoading, startFreeTrial, fetchTrialStatus, hasActiveSubscription]);

  return (
    <TrialContext.Provider value={contextValue}>
      {children}
    </TrialContext.Provider>
  );
};

export const useTrialContext = () => {
  const context = useContext(TrialContext);
  if (context === undefined) {
    throw new Error('useTrialContext must be used within a TrialProvider');
  }
  return context;
};
